import type { CSSProperties, ReactElement } from "react";

/**
 * The Garagevo icon set (direction "A · Munkalap", owner 2026-11-03): our own
 * filled drawings on a 48 x 48 grid, an ink body with one evo green highlight,
 * square ends, no shadows or gradients. Free to use, no icon library.
 *
 * The colours come from CSS variables, so a dark band or an active menu button
 * can turn them around: --ico-body (ink), --ico-hole (fog, the cut outs),
 * --ico-accent (evo green). `tone` changes only the highlight: orange for
 * waiting, red for an error.
 */
const B = "var(--ico-body, #0E2433)";
const H = "var(--ico-hole, #F2F5F7)";
const A = "var(--ico-accent, #10B89A)";
const O = "#FF6B1A";

const ICONS = {
  // Munkalap
  workOrder: (
    <><path d="M10 4h20l8 8v32H10z" style={{ fill: B }} /><path d="M30 4v8h8" opacity=".35" style={{ fill: H }} /><rect x="16" y="18" width="16" height="4" style={{ fill: H }} /><rect x="16" y="26" width="16" height="4" style={{ fill: H }} /><rect x="16" y="34" width="9" height="4" style={{ fill: A }} /></>
  ),
  // Árajánlat
  quote: (
    <><path d="M10 4h28v40l-7-4-7 4-7-4-7 4z" style={{ fill: B }} /><rect x="16" y="13" width="16" height="4" style={{ fill: H }} /><rect x="16" y="21" width="16" height="4" style={{ fill: H }} /><path d="M16 31l4 4 9-9" strokeWidth="4" fill="none" style={{ stroke: A }} /></>
  ),
  // Ügyfél
  customer: (
    <><circle cx="24" cy="15" r="9" style={{ fill: B }} /><path d="M7 44c0-10 7-16 17-16s17 6 17 16z" style={{ fill: B }} /><rect x="20" y="31" width="8" height="5" style={{ fill: A }} /></>
  ),
  // Jármű
  vehicle: (
    <><path d="M6 24l6-12h24l6 12v12H6z" style={{ fill: B }} /><rect x="14" y="15" width="20" height="8" style={{ fill: H }} /><rect x="4" y="36" width="12" height="7" style={{ fill: B }} /><rect x="32" y="36" width="12" height="7" style={{ fill: B }} /><rect x="9" y="27" width="6" height="4" style={{ fill: A }} /><rect x="33" y="27" width="6" height="4" style={{ fill: A }} /></>
  ),
  // Rendszám
  plate: (
    <><rect x="3" y="14" width="42" height="20" style={{ fill: B }} /><rect x="3" y="14" width="9" height="20" style={{ fill: A }} /><rect x="16" y="21" width="5" height="6" style={{ fill: H }} /><rect x="24" y="21" width="5" height="6" style={{ fill: H }} /><rect x="32" y="21" width="9" height="6" style={{ fill: H }} /></>
  ),
  // Forgalmi
  registration: (
    <><rect x="4" y="8" width="40" height="32" style={{ fill: B }} /><rect x="9" y="14" width="11" height="14" style={{ fill: A }} /><rect x="24" y="14" width="15" height="4" style={{ fill: H }} /><rect x="24" y="22" width="15" height="4" style={{ fill: H }} /><rect x="9" y="32" width="30" height="4" style={{ fill: H }} /></>
  ),
  // Emelő
  lift: (
    <><rect x="4" y="40" width="40" height="5" style={{ fill: B }} /><rect x="8" y="8" width="6" height="32" style={{ fill: B }} /><rect x="34" y="8" width="6" height="32" style={{ fill: B }} /><rect x="5" y="20" width="38" height="5" style={{ fill: A }} /></>
  ),
  // Olajcsere
  oilChange: (
    <><path d="M6 20h24l8 8v14H6z" style={{ fill: B }} /><path d="M30 20l11-8 3 4-9 8z" style={{ fill: B }} /><rect x="14" y="13" width="9" height="7" style={{ fill: B }} /><path d="M41 26c0 3-2 6-2 6s-2-3-2-6a2 2 0 0 1 4 0z" style={{ fill: A }} /><rect x="12" y="29" width="12" height="6" style={{ fill: A }} /></>
  ),
  // Nyomaték
  torque: (
    <><path d="M6 38l20-20 6 6-20 20H6z" style={{ fill: B }} /><path d="M28 4a12 12 0 1 0 16 16l-8-2-6-6z" style={{ fill: B }} /><circle cx="34" cy="14" r="4" style={{ fill: A }} /></>
  ),
  // Fék
  brake: (
    <><circle cx="24" cy="24" r="19" style={{ fill: B }} /><circle cx="24" cy="24" r="7" style={{ fill: H }} /><path d="M8 14a19 19 0 0 1 12-9l2 6a13 13 0 0 0-8 6z" style={{ fill: A }} /></>
  ),
  // Gumi
  tyre: (
    <><circle cx="24" cy="24" r="20" style={{ fill: B }} /><circle cx="24" cy="24" r="11" style={{ fill: H }} /><circle cx="24" cy="24" r="5" style={{ fill: A }} /></>
  ),
  // Akkumulátor
  battery: (
    <><rect x="4" y="12" width="40" height="28" style={{ fill: B }} /><rect x="9" y="7" width="8" height="5" style={{ fill: B }} /><rect x="31" y="7" width="8" height="5" style={{ fill: B }} /><rect x="10" y="22" width="9" height="4" style={{ fill: A }} /><rect x="12.5" y="19.5" width="4" height="9" style={{ fill: A }} /><rect x="29" y="22" width="9" height="4" style={{ fill: H }} /></>
  ),
  // Szűrő
  filter: (
    <><rect x="10" y="4" width="28" height="8" style={{ fill: B }} /><rect x="12" y="14" width="24" height="30" style={{ fill: B }} /><rect x="16" y="18" width="3" height="22" style={{ fill: H }} /><rect x="22.5" y="18" width="3" height="22" style={{ fill: A }} /><rect x="29" y="18" width="3" height="22" style={{ fill: H }} /></>
  ),
  // Készlet
  stock: (
    <><rect x="4" y="24" width="18" height="18" style={{ fill: B }} /><rect x="26" y="24" width="18" height="18" style={{ fill: B }} /><rect x="15" y="4" width="18" height="18" style={{ fill: B }} /><rect x="21" y="4" width="6" height="7" style={{ fill: A }} /><rect x="10" y="24" width="6" height="7" style={{ fill: H }} /><rect x="32" y="24" width="6" height="7" style={{ fill: H }} /></>
  ),
  // Bevételezés
  goodsIn: (
    <><path d="M4 20h40v24H4z" style={{ fill: B }} /><path d="M4 20l6-10h28l6 10z" opacity=".75" style={{ fill: B }} /><path d="M24 2v18M17 13l7 7 7-7" strokeWidth="5" fill="none" style={{ stroke: A }} /></>
  ),
  // Időpont
  appointment: (
    <><rect x="4" y="8" width="40" height="36" style={{ fill: B }} /><rect x="11" y="3" width="5" height="10" style={{ fill: B }} /><rect x="32" y="3" width="5" height="10" style={{ fill: B }} /><rect x="4" y="16" width="40" height="3" style={{ fill: H }} /><rect x="10" y="24" width="7" height="6" style={{ fill: H }} /><rect x="20.5" y="24" width="7" height="6" style={{ fill: A }} /><rect x="31" y="24" width="7" height="6" style={{ fill: H }} /><rect x="10" y="33" width="7" height="6" style={{ fill: H }} /></>
  ),
  // Emlékeztető
  reminder: (
    <><path d="M10 36V22a14 14 0 0 1 28 0v14l4 4H6z" style={{ fill: B }} /><rect x="19" y="41" width="10" height="5" style={{ fill: B }} /><circle cx="37" cy="10" r="7" style={{ fill: A }} /></>
  ),
  // Esedékes szerviz
  dueService: (
    <><circle cx="24" cy="25" r="19" style={{ fill: B }} /><rect x="22" y="12" width="5" height="15" style={{ fill: H }} /><rect x="22" y="23" width="12" height="5" style={{ fill: A }} /><rect x="19" y="2" width="11" height="5" style={{ fill: B }} /></>
  ),
  // Típusadatlap
  dataSheet: (
    <><path d="M24 3l19 11v22L24 47 5 36V14z" style={{ fill: B }} /><circle cx="24" cy="25" r="10" style={{ fill: H }} /><circle cx="24" cy="25" r="5" style={{ fill: A }} /></>
  ),
  // Kérdezd az MI-t
  askAi: (
    <><path d="M4 6h40v28H22l-10 9v-9H4z" style={{ fill: B }} /><path d="M24 11l2.5 6.5L33 20l-6.5 2.5L24 29l-2.5-6.5L15 20l6.5-2.5z" style={{ fill: A }} /></>
  ),
  // Fotó
  photo: (
    <><rect x="4" y="12" width="40" height="30" style={{ fill: B }} /><rect x="15" y="6" width="18" height="6" style={{ fill: B }} /><circle cx="24" cy="27" r="9" style={{ fill: H }} /><circle cx="24" cy="27" r="5" style={{ fill: A }} /></>
  ),
  // Számla
  invoice: (
    <><path d="M10 4h28v40H10z" style={{ fill: B }} /><rect x="15" y="10" width="18" height="4" style={{ fill: H }} /><rect x="15" y="18" width="12" height="4" style={{ fill: H }} /><rect x="15" y="31" width="18" height="8" style={{ fill: A }} /></>
  ),
  // Fizetés
  payment: (
    <><rect x="3" y="11" width="42" height="27" style={{ fill: B }} /><rect x="3" y="16" width="42" height="5" style={{ fill: H }} /><rect x="8" y="28" width="12" height="5" style={{ fill: A }} /></>
  ),
  // Garancia
  warranty: (
    <><path d="M24 3l18 7v12c0 12-8 19-18 23C14 41 6 34 6 22V10z" style={{ fill: B }} /><path d="M15 23l6 6 12-12" strokeWidth="5" fill="none" style={{ stroke: A }} /></>
  ),
  // Kulcsátadás
  keyHandover: (
    <><circle cx="15" cy="17" r="12" style={{ fill: B }} /><circle cx="15" cy="17" r="4.5" style={{ fill: H }} /><path d="M24 24l18 18-4 4-3-3-3 3-3-3-3 3-6-6z" style={{ fill: B }} /><rect x="30" y="4" width="14" height="12" style={{ fill: A }} /></>
  ),
  // Kész
  done: (
    <><rect x="4" y="4" width="40" height="40" style={{ fill: A }} /><path d="M13 24l8 8 15-15" strokeWidth="6" fill="none" style={{ stroke: H }} /></>
  ),
  // Figyelmeztetés
  warning: (
    <><path d="M24 3l22 40H2z" style={{ fill: O }} /><rect x="21.5" y="16" width="5" height="14" style={{ fill: B }} /><rect x="21.5" y="33" width="5" height="5" style={{ fill: B }} /></>
  ),
  // Felhasználók
  users: (
    <><circle cx="17" cy="14" r="7" style={{ fill: B }} /><path d="M3 40c0-9 6-14 14-14s14 5 14 14z" style={{ fill: B }} /><circle cx="34" cy="16" r="6" style={{ fill: A }} /><path d="M33 26c7 0 12 4 12 12H34c0-5-2-9-5-11z" style={{ fill: A }} /></>
  ),
  // Beállítások
  settings: (
    <><rect x="4" y="8" width="40" height="5" style={{ fill: B }} /><rect x="4" y="22" width="40" height="5" style={{ fill: B }} /><rect x="4" y="36" width="40" height="5" style={{ fill: B }} /><rect x="28" y="4" width="8" height="13" style={{ fill: A }} /><rect x="10" y="18" width="8" height="13" style={{ fill: B }} /><rect x="22" y="32" width="8" height="13" style={{ fill: B }} /></>
  ),
  // Kimutatás
  report: (
    <><rect x="4" y="42" width="40" height="3" style={{ fill: B }} /><rect x="7" y="26" width="8" height="14" style={{ fill: B }} /><rect x="20" y="16" width="8" height="24" style={{ fill: B }} /><rect x="33" y="6" width="8" height="34" style={{ fill: A }} /></>
  ),
  // Keresés
  search: (
    <><circle cx="20" cy="20" r="15" style={{ fill: B }} /><circle cx="20" cy="20" r="8" style={{ fill: H }} /><path d="M30 30l13 13" strokeWidth="7" style={{ stroke: B }} /><rect x="16" y="16" width="8" height="8" style={{ fill: A }} /></>
  ),
  // Nyomtatás
  print: (
    <><rect x="12" y="4" width="24" height="10" style={{ fill: B }} /><rect x="4" y="16" width="40" height="18" style={{ fill: B }} /><rect x="12" y="28" width="24" height="16" strokeWidth="3" style={{ fill: H, stroke: B }} /><rect x="36" y="20" width="4" height="4" style={{ fill: A }} /></>
  ),
} satisfies Record<string, ReactElement>;

export type IconName = keyof typeof ICONS;
export const ICON_NAMES = Object.keys(ICONS) as IconName[];

const TONES = { ok: "#10B89A", wait: "#FF6B1A", error: "#D7263D" } as const;

/** Menu keys and their icons (the main menu and the phone's bottom bar). */
export const NAV_ICONS: Record<string, IconName> = {
  dashboard: "lift",
  workOrders: "workOrder",
  customers: "customer",
  vehicles: "vehicle",
  inventory: "stock",
  invoices: "invoice",
  reminders: "reminder",
  import: "goodsIn",
  feedback: "askAi",
  settings: "settings",
  operator: "report",
  subscription: "payment",
  legal: "dataSheet",
};

export function Icon({ name, size = 28, tone, className, label }: { name: IconName; size?: number; tone?: keyof typeof TONES; className?: string; label?: string }) {
  return (
    <svg
      className={["gi", className].filter(Boolean).join(" ")}
      width={size}
      height={size}
      viewBox="0 0 48 48"
      focusable="false"
      {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
      style={tone ? ({ "--ico-accent": TONES[tone] } as CSSProperties) : undefined}
      data-icon={name}
    >
      {ICONS[name]}
    </svg>
  );
}

/** A menu item's icon by its key (unknown keys get the work order sheet). */
export function NavIcon({ name, size }: { name: string; size?: number }) {
  return <Icon name={NAV_ICONS[name] ?? "workOrder"} size={size} />;
}
